import { TableFilterOption, TableFilterOptionValue, TableFilterState } from '../../../table/table.component';
import { IPersistentFilterColumn } from '../../../../interfaces/IPersistentFilterColumnSetting';
import { IPersistentFilterUpdateBody } from '../../../../interfaces/IPersistentFilterUpdateBody';
import { PersistentFilterProperties } from '../../../../enums/PersistentFilterProperties.enum';
import { IPersistentFilter } from '../../../../interfaces/employee/IPersistentFilter';
import { IOrgUnit } from '../../../../interfaces/orgunit/IOrgUnit';
import { IEmployeeListItem } from '../../../../interfaces/IEmployeeListItem';
import { IFilter } from '../../../../interfaces/IFilter';

export class EmployeePickerOperations {
  public static getUpdatedPreSelectedPersistentFilters(tableFilterState: TableFilterState): IPersistentFilterColumn {
    return Object.keys(tableFilterState).reduce(
      (updatedPreSelectedPersistentFilters: IPersistentFilterColumn, key: TableFilterOptionValue) => {
        updatedPreSelectedPersistentFilters[key] = tableFilterState[key].options.map(option => {
          return { selected: true, name: option, title: option };
        });
        return updatedPreSelectedPersistentFilters;
      },
      {},
    );
  }

  public static getPersistentFilterBody(
    updatedPreSelectedPersistentFilters: IPersistentFilterColumn,
    scdGid: string,
  ): IPersistentFilterUpdateBody {
    return {
      filterView: {
        controllingFilter: updatedPreSelectedPersistentFilters ? JSON.stringify(updatedPreSelectedPersistentFilters) : null,
        scdGid,
      },
      propertiesToUpdate: [PersistentFilterProperties.CONTROLLING_FILTER],
    };
  }

  public static initializePersistentFilters(userPersistentFilters: IPersistentFilter): TableFilterState {
    if (userPersistentFilters.controllingFilter) {
      const parsedPersistentFilters: IPersistentFilterColumn = JSON.parse(userPersistentFilters.controllingFilter);
      return Object.keys(parsedPersistentFilters).reduce((result: TableFilterState, key: string) => {
        result[key] = {
          sort: undefined,
          options:
            key === 'retired'
              ? parsedPersistentFilters[key].map(persistentFilterColumnSetting =>
                  persistentFilterColumnSetting.name === 'watch_later'
                    ? 'true'
                    : persistentFilterColumnSetting.name === ' '
                      ? 'false'
                      : persistentFilterColumnSetting.name,
                )
              : parsedPersistentFilters[key].map(persistentFilterColumnSetting => persistentFilterColumnSetting.name),
        };
        return result;
      }, {});
    }
    return null;
  }

  public static extractFiltersFromTableFilterState(tableFilterState: TableFilterState): IFilter[] {
    if (!tableFilterState) {
      return [];
    }

    return Object.keys(tableFilterState)
      .filter(key => tableFilterState[key].options && tableFilterState[key].options.length > 0)
      .map(key => {
        return {
          key,
          values: tableFilterState[key].options,
        };
      });
  }

  public static transformFiltersToTableFilterState(filters: IFilter[]): TableFilterState {
    const tableFilterState: TableFilterState = {};

    filters.forEach(filter => {
      if (filter.values.length > 0) {
        tableFilterState[filter.key] = {
          sort: undefined,
          options: filter.values,
        };
      }
    });

    return tableFilterState;
  }

  public static transformOrgUnitsToMap(orgUnits: IOrgUnit[]): Map<string, string[]> {
    return orgUnits.reduce((map: Map<string, string[]>, item: IOrgUnit) => {
      const { groupName, team } = item;
      map.set(groupName, [...(map.get(groupName) || []), team]);
      return map;
    }, new Map<string, string[]>());
  }

  public static getPreSelectedTeamFilters(
    preSelectedPersistentFilters: TableFilterState,
    orgUnitsMap: Map<TableFilterOptionValue, string[]>,
  ): TableFilterOption[] {
    if (preSelectedPersistentFilters && preSelectedPersistentFilters?.groupName && preSelectedPersistentFilters?.groupName.options.length) {
      return preSelectedPersistentFilters.groupName.options
        .flatMap(option => orgUnitsMap.get(option))
        .map(orgUnitTeamName => {
          return { value: orgUnitTeamName, label: orgUnitTeamName };
        });
    }
    return Array.from(orgUnitsMap.values())
      .flat()
      .map(orgUnitTeamName => {
        return { value: orgUnitTeamName, label: orgUnitTeamName };
      });
  }

  public static filterEmployeesBySearchInput(search: string, employees: IEmployeeListItem[]): IEmployeeListItem[] {
    return employees.filter(employee => {
      const includesId = `${employee.id}`.toLowerCase().includes(search.toLowerCase());
      const includesName = employee.scdName.toLowerCase().includes(search.toLowerCase());
      const includesContractTypeName = employee.contractTypeName.toLowerCase().includes(search.toLowerCase());
      const includesGroupName = employee.groupName.toLowerCase().includes(search.toLowerCase());
      const includesTeamName = employee.teamName.toLowerCase().includes(search.toLowerCase());
      const includesOrgTeamName = employee.orgTeamName.toLowerCase().includes(search.toLowerCase());

      return includesId || includesOrgTeamName || includesTeamName || includesGroupName || includesContractTypeName || includesName;
    });
  }

  public static getLimitedFilterOptions(employeePickerData: IFilter): TableFilterOption[] {
    return employeePickerData.values.map(option => {
      return {
        value: option?.toString(),
        label: option?.toString(),
      };
    });
  }
}
